import "./modal-swiper.js";

(function () {
  "use strict";

  if (!customElements.get("product-video-reels")) {
    class ProductVideoReels extends HTMLElement {
      constructor() {
        super();
        this.swiperContainer = this.querySelector(".wt-slider__container");
        this.modalOverlay = null;
        this.slides = []; // { src, cardHTML, item }
        this.currentIndex = 0;
        this.pageBody = document.querySelector("body");
        this.bodyActiveModalClass = "video-reels-modal-opened";
        this.videoObserver = null;
      }

      connectedCallback() {
        const ds = this.dataset;

        this.options = {
          autoAdvanceOnEnd: ds.autoAdvanced === "true",
          showUnmute: ds.showUnmute === "true",
          showPlayPause: ds.showPlayPause === "true",
          showQuickAdd: ds.showQuickAdd === "true",
          threshold: 5,
        };

        this.locale = {
          prev: ds.localePrev,
          next: ds.localeNext,
          close: ds.localeClose,
          swipe: ds.localeSwipe,
        };
        this.onSwiperReady = () => this.checkSwiperInit();
        this.addEventListener('theme:swiper-ready', this.onSwiperReady);
        if (this.slidesInitialized) this.initLazyVideos();
        this.checkSwiperInit();
      }

      disconnectedCallback() {
        this.removeEventListener('theme:swiper-ready', this.onSwiperReady);
        this.videoObserver?.disconnect();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.stopPreview();
      }

      checkSwiperInit() {
        this.swiperContainer = this.querySelector('[data-swiper]');
        if (
          !this.slidesInitialized && this.swiperContainer &&
          this.swiperContainer.classList.contains("swiper-initialized")
        ) {
          this.slidesInitialized = true;
          this.initSlides();
          this.initItemClicks();
          this.initLazyVideos();
        }
      }

      initSlides() {
        this.slides = Array.from(
          this.querySelectorAll(".wt-product-video-reels__item"),
        ).map((item) => {
          const videoEl = item.querySelector("video");
          const src =
            videoEl?.dataset.modalSrc ||
            videoEl?.querySelector("source")?.dataset.src ||
            videoEl?.querySelector("source")?.src ||
            videoEl?.currentSrc ||
            "";
          const cardEl = item.querySelector(".shoppable-product-card");

          if (!this.options.showQuickAdd)
            cardEl?.classList.add("shoppable-product-card--disable-quick-add");

          return { src, cardHTML: cardEl?.outerHTML || "", item };
        });
      }

      initItemClicks() {
        if (this.dataset.disableModal === "true") {
          // A newly uploaded reel may have no compact rendition yet. Keep it a
          // poster until deliberately played, even on inline-only PDPs.
          this.slides.forEach((slide) => {
            const video = slide.item.querySelector('video');
            if (!video || video.querySelector('source') || !slide.src) return;
            slide.item.tabIndex = 0;
            slide.item.setAttribute('role', 'button');
            slide.item.setAttribute('aria-label', 'Reproducir testimonio');
            const play = () => {
              if (video.querySelector('source')) return;
              this.stopPreview();
              const source = document.createElement('source');
              source.dataset.src = slide.src;
              source.type = 'video/mp4';
              video.appendChild(source);
              video.controls = true;
              this.activePreview = video;
              this.hydrateVideo(video);
              video.play().catch(() => {});
            };
            slide.item.addEventListener('click', play);
            slide.item.addEventListener('keydown', (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                play();
              }
            });
          });
          return;
        }

        this.slides.forEach((slide, idx) => {
          slide.item.style.cursor = "pointer";
          slide.item.addEventListener("click", () => this.openModalAt(idx));
        });
      }

      initLazyVideos() {
        const videos = Array.from(
          this.querySelectorAll('video[data-lazy-autoplay="true"]'),
        );

        if (!videos.length) return;

        this.videoVisibility = new Map();
        this.onVisibilityChange = () => this.syncPreview();
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        if (!("IntersectionObserver" in window)) return;
        this.videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting && video.dataset.poster) {
                video.poster = video.dataset.poster;
                delete video.dataset.poster;
              }
              this.videoVisibility.set(video, entry.intersectionRatio);
            });
            this.syncPreview();
          },
          { rootMargin: "0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
        );

        videos.forEach((video) => this.videoObserver.observe(video));
      }

      stopPreview() {
        const video = this.activePreview;
        if (!video) return;
        this.activePreview = null;
        video.pause();
        video.querySelectorAll('source[src]').forEach(source => source.removeAttribute('src'));
        video.load(); // Cancel buffering as soon as the preview leaves the viewport.
      }

      syncPreview() {
        const saveData = navigator.connection?.saveData;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let next = null;
        let largestRatio = 0.5;
        if (!document.hidden && !saveData && !reducedMotion && !this.modal) {
          this.videoVisibility?.forEach((ratio, video) => {
            if (ratio >= largestRatio && video.querySelector('source[data-src]')) {
              next = video;
              largestRatio = ratio;
            }
          });
        }
        if (next === this.activePreview) return;
        this.stopPreview();
        if (next) {
          this.activePreview = next;
          this.hydrateVideo(next);
          next.play().catch(() => {});
        }
      }

      hydrateVideo(video) {
        if (video.dataset.poster) {
          video.poster = video.dataset.poster;
          delete video.dataset.poster;
        }

        const sources = Array.from(video.querySelectorAll("source[data-src]"));
        if (!sources.length) return;

        sources.forEach((source) => {
          source.src = source.dataset.src;
        });
        video.muted = true;
        video.load();
      }

      openModalAt(index) {
        this.stopPreview();
        this.currentIndex = index;
        // build your slides array once at initSlides()
        // e.g. [{ src, cardHTML }, …]
        this.modal = document.createElement("modal-swiper");
        this.modal.addEventListener('reel-modal-closed', () => {
          this.modal = null;
          this.syncPreview();
        }, { once: true });
        this.modal.slides = this.slides; // pass the data
        this.modal.config = {
          initialSlide: index,
          slidesPerView: 1,
          centeredSlides: true,
          direction: "vertical",
          loop: true,
          navigation: {
            nextEl: ".video-reels-modal__nav--next",
            prevEl: ".video-reels-modal__nav--prev",
          },
          pagination: false,
          locale: this.locale,
          ...this.options,
        };
        this.modal.open();
      }

      navigate(delta) {
        this.currentIndex =
          (this.currentIndex + delta + this.slides.length) % this.slides.length;
        const { src, cardHTML } = this.slides[this.currentIndex];
        const vid = this.modalOverlay.querySelector(
          ".video-reels-modal__video",
        );
        vid.pause();
        vid.src = src;
        vid.load();
        vid.play();
        this.modalOverlay.querySelector(".video-reels-modal__card").innerHTML =
          cardHTML;
      }

      closeModal() {
        if (!this.modalOverlay) return;
        const vid = this.modalOverlay.querySelector("video");
        if (vid) {
          vid.pause();
          vid.currentTime = 0;
        }
        this.modalOverlay.remove();
        this.modalOverlay = null;
        this.pageBody.classList.remove(this.bodyActiveModalClass);
      }
    }

    customElements.define("product-video-reels", ProductVideoReels);
  }
})();
